import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search as SearchIcon, X, Share2 } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api } from "@/lib/api";
import { usePageContent } from "@/lib/content";
import { CTABlock } from "@/components/common/CTABlock";
import { absUrl } from "@/components/admin/UploadWidget";

export function Blog() {
    useSEO({
        title: "Tech Blog | RKS Systems",
        description: "Practical engineering, cloud, data and AI insights from RKS Systems technology leaders.",
    });
    const pc = usePageContent("blog");
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("all");
    const load = () => {
        const params = {};
        if (q) params.q = q;
        if (category !== "all") params.category = category;
        api.get("/blogs", { params }).then(({ data }) => setItems(data.items || []));
    };
    useEffect(() => { load(); }, [category]);
    const cats = ["all", ...Array.from(new Set(items.map((i) => i.category)))];
    return (
        <div data-testid="page-blog">
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-24 lg:py-32">
                    <div className="max-w-3xl">
                        <span className="overline text-primary">Tech blog</span>
                        <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">
                            {pc?.hero_headline || "Engineering Insights: Agentic AI, Cloud Modernization & Automation"}
                        </h1>
                        <p className="mt-6 text-lg text-white/70 leading-relaxed">
                            {pc?.hero_subheadline || "In-depth technical essays, reference architectures, and practical guides from RKS Systems. Master enterprise AI deployment, intelligent process automation, Cloud & FinOps strategies, and modern software architecture."}
                        </p>
                    </div>
                </div>
            </section>
            <section className="py-16 lg:py-20">
                <div className="container-app">
                    <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
                        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 flex items-center gap-3 rounded-md border border-border bg-white px-4 py-3">
                            <SearchIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                            <input
                                data-testid="blog-search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search articles..."
                                className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                            />
                            {q && <button type="button" onClick={() => { setQ(""); setTimeout(load, 0); }}><X className="h-4 w-4 text-muted-foreground hover:text-foreground" /></button>}
                            <button type="submit" data-testid="blog-search-submit" className="text-sm font-semibold text-primary hover:underline">Search</button>
                        </form>
                        <div className="flex flex-wrap gap-2">
                            {cats.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCategory(c)}
                                    data-testid={`blog-filter-${c}`}
                                    className={`text-xs font-semibold uppercase tracking-wider px-3.5 py-2 rounded-full border transition-all ${
                                        category === c ? "bg-primary text-primary-foreground border-primary" : "bg-white text-foreground/70 border-border hover:border-primary/40"
                                    }`}
                                >
                                    {c === "all" ? "All" : c}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((b) => (
                            <Link key={b.id} to={`/blog/${b.id}`} data-testid={`blog-card-${b.id}`}
                                className="group overflow-hidden rounded-xl border border-border bg-card transition-all hover:shadow-lg hover:-translate-y-1">
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={absUrl(b.cover_image)} alt={b.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                </div>
                                <div className="p-6">
                                    <div className="text-xs font-semibold text-primary tracking-wider uppercase">{b.category}</div>
                                    <h3 className="mt-2 font-heading text-lg font-bold text-foreground leading-tight">{b.title}</h3>
                                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{b.excerpt}</p>
                                    <div className="mt-4 text-xs text-muted-foreground">{b.author} · {new Date(b.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

export function BlogDetail() {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [notFound, setNotFound] = useState(false);
    useSEO({
        title: item ? `${item.title} | RKS Systems` : "Blog | RKS Systems",
        description: item?.excerpt,
        jsonLd: item ? {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: item.title,
            author: item.author,
            image: item.cover_image,
            datePublished: item.published_at,
            description: item.excerpt,
        } : undefined,
    });
    useEffect(() => {
        api.get(`/blogs/${id}`).then(({ data }) => setItem(data)).catch(() => setNotFound(true));
    }, [id]);
    if (notFound) return <Navigate to="/blog" replace />;
    if (!item) return <div className="container-app py-24 text-center text-muted-foreground">Loading...</div>;

    const share = () => {
        const url = window.location.href;
        if (navigator.share) navigator.share({ title: item.title, url }).catch(() => {});
        else navigator.clipboard?.writeText(url);
    };

    return (
        <div data-testid={`page-blog-${item.id}`}>
            <article className="container-app max-w-3xl py-16 lg:py-24">
                <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8"><ArrowLeft className="h-3.5 w-3.5" /> All articles</Link>
                <div className="text-xs font-semibold text-primary tracking-wider uppercase">{item.category}</div>
                <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">{item.title}</h1>
                <div className="mt-5 flex items-center gap-3 text-sm text-muted-foreground">
                    <span>{item.author}</span>
                    <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                    <span>{new Date(item.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
                    <button data-testid="blog-share-btn" onClick={share} className="ml-auto inline-flex items-center gap-1.5 text-primary hover:underline font-semibold">
                        <Share2 className="h-3.5 w-3.5" /> Share
                    </button>
                </div>
                <div className="mt-8 aspect-[16/9] overflow-hidden rounded-xl border border-border">
                    <img src={absUrl(item.cover_image)} alt={item.title} className="h-full w-full object-cover" />
                </div>
                <div className="mt-10 prose prose-lg max-w-none text-foreground/85 leading-relaxed whitespace-pre-line">
                    {item.content}
                </div>
            </article>
            <CTABlock />
        </div>
    );
}
